<template>
  <div>
    <PageHeader :items="items" />
    <validation-observer ref="simpleRules">
      <div class="card">
        <div class="card-body">
          <b-row>
            <b-col
              cols="12"
              lg="6"
              md="6"
              class="mb-1"
            >
              <validation-provider
                #default="{ errors }"
                name="Name"
                rules="required"
              >
                <TextInput
                  v-model="form.name"
                  :label="$t('name')"
                  :required="true"
                  :error="errors[0]"
                  :maxLength="255"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-col>
            <b-col
              cols="12"
              lg="6"
              md="6"
              class="mb-1"
            >
              <TextInput
                v-model="form.price"
                type="number"
                :min="0"
                :label="$t('price')"
                :disabled="true"
              />
            </b-col>
            <b-col
              cols="12"
              lg="6"
              md="6"
              class="mb-1"
            >
                <TextInput
                  v-model="form.units"
                  type="number"
                  :min="0"
                  :label="$t('units')"
                  :disabled="true"
                  :limitToPositiveNumbers="true"
                />
            </b-col>
            <b-col
              cols="12"
              lg="6"
              md="6"
              class="mb-1"
            >
                <TextInput
                  v-model="form.price_per_unit"
                  type="number"
                  :min="0"
                  :disabled="true"
                  :label="$t('pricePerUnit')"
                />
            </b-col>
            <b-col
              cols="12"
              lg="6"
              md="6"
              class="mb-1"
            >
                <TextInput
                  v-model="form.dentawax_amount"
                  type="number"
                  :min="0"
                  :disabled="true"
                  :label="$t('dentawaxAmount')"
                />
            </b-col>
            <b-col
              cols="12"
              lg="6"
              md="6"
              class="mb-1"
            >
                <TextInput
                  v-model="form.pay_as_you_go_amount"
                  type="number"
                  :min="0"
                  :disabled="true"
                  :label="$t('payAsYouGoAmount')"
                />
            </b-col>
            <b-col
              cols="12"
              lg="6"
              md="6"
              class="mb-1"
            >
                <TextInput
                  v-model="form.pay_as_you_go_units"
                  type="number"
                  :min="0"
                  :disabled="true"
                  :label="$t('payAsYouGoUnits')"
                  :limitToPositiveNumbers="true"
                />
            </b-col>
            <b-col
              cols="12"
              lg="6"
              md="6"
              class="mb-1"
            >
              <label
                class="input-label form-label"
                for="is-active"
              >{{
                $t("isActive")
              }}</label>
              <input
                id="is-active"
                v-model="form.is_active"
                class="form-check-input-custom ml-2"
                type="checkbox"
              >
            </b-col>
            <b-col
              cols="12"
              class="mb-1"
            >
              <validation-provider
                #default="{ errors }"
                name="Description"
                rules="required"
              >
                <label class="input-label form-label">
                  {{ $t("description") }}<span style="color: red">&nbsp;*</span>
                </label>
                <textarea
                  v-model="form.description"
                  class="form-control"
                  rows="5"
                  :class="{ 'is-invalid': errors[0] }"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-col>
          </b-row>
        </div>
      </div>
      <div class="d-flex align-items-center justify-content-end gap-2">
        <router-link
          to="/plans"
          class="d-flex align-items-center gap-1 btn btn-secondary"
        >
          <feather-icon
            icon="XIcon"
            size="12"
          />
          <span>{{ $t("cancel") }}</span>
        </router-link>
        <b-button
          v-if="$can(`${$route.meta.permission}.edit`)"
          variant="primary"
          class="d-flex align-items-center gap-1"
          :disabled="isFormInvalid"
          @click="updatePlan"
        >
          <feather-icon
            icon="PlusIcon"
            size="12"
          />
          <span>{{ $t("updatePlan") }}</span>
        </b-button>
      </div>
    </validation-observer>
  </div>
</template>

<script>
import TextInput from '@/components/TextInput.vue'
import PageHeader from '@/components/PageHeader.vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { required, positive, integer } from '@validations'
import formDirtyMixin from '@/mixins/formDirtyMixin'

export default {
  components: {
    TextInput,
    PageHeader,
    ValidationProvider,
    ValidationObserver,
  },
  mixins: [formDirtyMixin],
  data() {
    return {
      originalForm: null,
      form: {
        name: '',
        description: '',
        price: '',
        units: '',
        price_per_unit: '',
        dentawax_amount: '',
        pay_as_you_go_amount: '',
        pay_as_you_go_units: '',
        is_active: true,
      },
    }
  },
  computed: {
    items() {
      return [
        {
          text: 'Dental Twin',
          to: '/home',
        },
        {
          text: this.$t('plans'),
          to: '/plans',
        },
        {
          text: this.$t('edit'),
          active: true,
        },
      ]
    },
    isFormInvalid() {
      return [
        this.form.name,
        this.form.description,
        this.form.units,
        this.form.price_per_unit,
        this.form.dentawax_amount,
        this.form.pay_as_you_go_amount,
        this.form.pay_as_you_go_units,
      ].some(value => value === '' || value === null || value === undefined)
    },
  },
  watch: {
    form: {
      handler(newVal) {
        this.isFormDirty = JSON.stringify(newVal) !== this.originalForm
      },
      deep: true,
    },
  },
  async mounted() {
    try {
      this.$store.commit('showLoader', true)
      const res = await this.$store.dispatch('plans/show', this.$route.params.id)
      const response = res?.data?.data
      this.form.name = response?.name ?? ''
      this.form.description = response?.description ?? ''
      this.form.price = response?.price ?? ''
      this.form.units = response?.units ?? ''
      this.form.price_per_unit = response?.price_per_unit ?? response?.pricePerUnit ?? ''
      this.form.dentawax_amount = response?.dentawax_amount ?? response?.dentawaxAmount ?? ''
      this.form.pay_as_you_go_amount = response?.pay_as_you_go_amount ?? response?.payAsYouGoAmount ?? ''
      this.form.pay_as_you_go_units = response?.pay_as_you_go_units ?? response?.payAsYouGoUnits ?? ''
      this.form.is_active = response?.is_active ?? response?.isActive ?? true
      this.originalForm = JSON.stringify(this.form)
    } catch (e) {
      console.error('api error ___', e)
    } finally {
      this.$store.commit('showLoader', false)
    }
  },
  methods: {
    async updatePlan() {
      this.$refs.simpleRules.validate().then(async success => {
        if (success) {
          try {
            this.$store.commit('showLoader', true)
            const { price, ...data } = this.form
            await this.$store.dispatch('plans/update', {
              id: this.$route.params.id,
              data,
            })
            this.isFormDirty = false
            this.originalForm = JSON.stringify(this.form)
            this.$router.push('/plans')
          } catch (e) {
            console.error('api error ___', e)
          } finally {
            this.$store.commit('showLoader', false)
          }
        }
      })
    },
  },
}
</script>

<style scoped>
#nprogress {
  position: relative;
  z-index: 9999999;
}

.white-color {
  color: white !important;
}

.vgt-responsive {
  overflow-x: visible !important;
}
</style>
